export const IST_TIME_ZONE = 'Asia/Kolkata';
export const IST_OFFSET_MINUTES = 330;

const ISO_DATE_TIME_WITHOUT_ZONE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?$/;
const ISO_DATE_TIME_WITH_ZONE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:\d{2})$/;

function getIstFormatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_TIME_ZONE,
    ...options
  });
}

export function isIsoDateTimeWithoutZone(value: any): value is string {
  return typeof value === 'string' && ISO_DATE_TIME_WITHOUT_ZONE_REGEX.test(value);
}

export function isIsoDateTimeWithZone(value: any): value is string {
  return typeof value === 'string' && ISO_DATE_TIME_WITH_ZONE_REGEX.test(value);
}

export function normalizeUtcDateTimeString(value: any): any {
  if (!isIsoDateTimeWithoutZone(value)) {
    return value;
  }
  return `${value}Z`;
}

export function convertIstDateTimeStringToUtc(value: any): any {
  if (!isIsoDateTimeWithoutZone(value)) {
    return value;
  }
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(\.(\d{1,9}))?)?$/
  );
  if (!match) {
    return value;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6] || '0');
  const fractionRaw = match[8] || '';
  const millisecond = Number((fractionRaw + '000').slice(0, 3));
  const utcTimestamp = Date.UTC(year, month - 1, day, hour, minute, second, millisecond) - (IST_OFFSET_MINUTES * 60 * 1000);
  const utcDate = new Date(utcTimestamp);
  const utcYear = utcDate.getUTCFullYear();
  const utcMonth = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
  const utcDay = String(utcDate.getUTCDate()).padStart(2, '0');
  const utcHour = String(utcDate.getUTCHours()).padStart(2, '0');
  const utcMinute = String(utcDate.getUTCMinutes()).padStart(2, '0');
  const utcSecond = String(utcDate.getUTCSeconds()).padStart(2, '0');
  const fraction = fractionRaw ? `.${fractionRaw}` : '';
  const includeSeconds = !!match[6] || !!fractionRaw;
  return `${utcYear}-${utcMonth}-${utcDay}T${utcHour}:${utcMinute}${includeSeconds ? `:${utcSecond}${fraction}` : ''}`;
}

export function parseServerDate(value: any): Date | null {
  if (!value) {
    return null;
  }
  const normalizedValue = normalizeUtcDateTimeString(value);
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}

export function formatDateTimeInIst(value: any, includeSeconds: boolean = false): string {
  const date = parseServerDate(value);
  if (!date) {
    return '';
  }
  return getIstFormatter({
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: includeSeconds ? '2-digit' : undefined,
    hour12: false
  }).format(date).replace(',', '');
}

export function formatDateOnlyInIst(value: any): string {
  const date = parseServerDate(value);
  if (!date) {
    return '';
  }
  return getIstFormatter({
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
}

export function formatShortDateInIst(value: any): string {
  const date = parseServerDate(value);
  if (!date) {
    return '';
  }
  return getIstFormatter({
    day: '2-digit',
    month: 'short'
  }).format(date);
}

export function formatButtonDateInIst(value: any): string {
  const date = parseServerDate(value);
  if (!date) {
    return '';
  }
  return getIstFormatter({
    day: '2-digit',
    month: 'short'
  }).format(date).replace(',', '');
}

export function getTodayDateStringInIst(): string {
  return toIstDateInputValue(new Date()) || '';
}

export function toIstDateInputValue(value: any): string | null {
  const date = parseServerDate(value) || (value instanceof Date ? value : null);
  if (!date) {
    return null;
  }
  const parts = getIstFormatter({
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);
  const map = new Map(parts.map((part) => [part.type, part.value]));
  const year = map.get('year');
  const month = map.get('month');
  const day = map.get('day');
  if (!year || !month || !day) {
    return null;
  }
  return `${year}-${month}-${day}`;
}

export function getCurrentIstShiftedDate(): Date {
  return new Date(Date.now() + (IST_OFFSET_MINUTES * 60 * 1000));
}
